import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { UsuarioService } from '@/app/services/usuario.service';

export interface User {
  nombre: string;
  usuario: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  estacion: string;
  idRol: number;
  idUsuario: number;
  idEstacion: number;
}

@Injectable({
  providedIn: 'root'
})

export class TokenReaderService {

  private _usuario = signal<User | null>(null)
  readonly usuario = this._usuario.asReadonly()

  //se usa en: cargas
  readonly isAdmin = computed(() => {
    const user = this.usuario();
    return user ? String(user.idRol) === '1' : false;
  });


  //Rol solo de reportes
  readonly isReportUser = computed(() => {
    const user = this.usuario();
    return user ? Number(user.idRol) === 5 : false;
  });

  //se usa en: detalles-carga
  readonly isValid = computed(() => {
    const user = this.usuario();
    if (!user) return false;
    const rol = String(user.idRol);
    return rol === '1' || rol === '2';
  });

  private usuarioService = inject(UsuarioService);
  private auth0 = inject(AuthService);

  constructor() { }

  /**
   * Consulta la API (getUserInfo) con el correo del usuario y guarda la
   * respuesta en el signal `usuario`. Devuelve el Observable para que el
   * llamador pueda reaccionar (navegar, manejar errores, etc.).
   */
  loadUserData(): Observable<User | null> {
    return (this.usuarioService.getUserInfo() as Observable<User>).pipe(
      tap((data) => this._usuario.set(data ?? null))
    );
  }

  /**
   * Garantiza que los datos del usuario estén cargados en el signal. Si ya
   * existen, los devuelve; si no, obtiene el correo desde el id token de Auth0
   * y los solicita a la API (getUserInfo). Lo usan los guards antes de activar
   * una ruta para que los componentes tengan la info disponible tras un refresh.
   */
  ensureUserLoaded(): Observable<User | null> {
    const current = this._usuario();
    if (current) {
      return of(current);
    }

    return this.auth0.idTokenClaims$.pipe(
      take(1),
      switchMap((claims: any) => {
        if (!claims) {
          return of(null);
        }
        return this.loadUserData();
      })
    );
  }

  setUserData(user: User | null): void {
    this._usuario.set(user);
  }

  /**
   * Devuelve los datos del usuario actual (provenientes de getUserInfo) que se
   * almacenan en el signal. Sustituye la antigua decodificación del JWT de
   * localStorage: con Auth0 la identidad de negocio vive en este servicio.
   */
  getUserData(): any {
    return this._usuario();
  }

  clear(): void {
    this._usuario.set(null);
  }
}
